import * as React from 'react'

import type {
  SupplementMatchDataType,
  SupplementType,
  SupplementsMatches,
  WorksheetData,
} from 'modules/payroll_bundle/components/common/helper/worksheet'
import {
  ADD_SUPPLEMENT_TYPE,
  DELETE_SUPPLEMENT_TYPE,
  RESET_DATA,
  SHOW_ASSIGN_TAXONOMY_MODAL,
  SHOW_DELETE_MODAL,
  SHOW_MANAGED_IMPORTED_MODAL,
  SHOW_UPLOAD_MODAL,
  UPDATE_DATA,
  UPDATE_SUPPLEMENT_TYPE,
  UPLOAD_FILE,
} from 'modules/payroll_bundle/lib/constants'

export type ActionTypes =
  | 'RESET_DATA'
  | 'UPDATE_DATA'
  | 'ADD_SUPPLEMENT_TYPE'
  | 'UPDATE_SUPPLEMENT_TYPE'
  | 'DELETE_SUPPLEMENT_TYPE'
  | 'SHOW_UPLOAD_MODAL'
  | 'SHOW_ASSIGN_TAXONOMY_MODAL'
  | 'SHOW_DELETE_MODAL'
  | 'SHOW_MANAGED_IMPORTED_MODAL'

export interface ResetDataAction {
  type: 'RESET_DATA'
}
export interface UpdateDataAction {
  type: 'UPDATE_DATA'
  payload: Omit<Partial<State>, 'supplementDispatch'>
}

export interface AddSupplementTypeAction {
  type: 'ADD_SUPPLEMENT_TYPE'
  payload: SupplementType
}

export interface UpdateSupplementTypeAction {
  type: 'UPDATE_SUPPLEMENT_TYPE'
  payload: SupplementType
}

export interface DeleteSupplementTypeAction {
  type: 'DELETE_SUPPLEMENT_TYPE'
  payload: { id: string }
}

export interface ShowUploadModalAction {
  type: 'SHOW_UPLOAD_MODAL'
}

export interface ShowAssignTypeModalAction {
  type: 'SHOW_ASSIGN_TAXONOMY_MODAL'
}

export interface ShowDeleteModalAction {
  type: 'SHOW_DELETE_MODAL'
}

export interface ShowManageImportedModalAction {
  type: 'SHOW_MANAGED_IMPORTED_MODAL'
}

export type Actions =
  | ResetDataAction
  | UpdateDataAction
  | AddSupplementTypeAction
  | UpdateSupplementTypeAction
  | DeleteSupplementTypeAction
  | ShowUploadModalAction
  | ShowAssignTypeModalAction
  | ShowDeleteModalAction
  | ShowManageImportedModalAction

export interface ImportData {
  id: number
  employee_id: number
  userId: number
  full_name: string
  legal_entity_id: number
  legal_entity_name?: string
  contracts_taxonomy_id: number
  title: string
  // Amounts for each of the `supplementTypes`, indexed by the `SupplementType` `id`
  // (see `buildSupplementType`).
  // (These props are originally set in `matchedData`.)
  // Value is a string, the other types had to be added to make it compatible
  // with the other properties in the interface.
  [key: string]: string | number | undefined
}

export interface State {
  filename: string
  matchedData: SupplementMatchDataType[]
  activeButton: string
  selectedFields: string[]
  worksheetData: WorksheetData
  supplementTypes: SupplementType[]
  editingSupplementType: SupplementType | null
  worksheetMatches: SupplementsMatches
  showUploadModal: boolean
  showAssignTypeModal: boolean
  showDeleteModal: boolean
  showDeletePartialModal: boolean
  bulkImportToDeleteId: number
  showManageImportedModal: boolean
  selectedSupplements: Set<number>
  bulkImportSupplementStage: BulkImportSupplementStage
  importedSupplements: ImportData[]
  disableUploadButton: boolean
  supplementDispatch: React.Dispatch<Actions>
}

export type BulkImportSupplementStage = 'UPLOAD_FILE' | 'SELECT_DATA' | 'REVIEW_AND_IMPORT'

const initialState: State = {
  filename: '',
  matchedData: [],
  activeButton: '',
  selectedFields: [],
  worksheetData: [],
  supplementTypes: [],
  editingSupplementType: null,
  worksheetMatches: {},
  showUploadModal: false,
  showAssignTypeModal: false,
  showDeleteModal: false,
  showDeletePartialModal: false,
  bulkImportToDeleteId: 0,
  showManageImportedModal: false,
  selectedSupplements: new Set([]),
  importedSupplements: [],
  disableUploadButton: false,
  bulkImportSupplementStage: UPLOAD_FILE,
  supplementDispatch: () => null,
}

const bulkImportSupplementsContext = React.createContext<State | null>(null)

const BulkImportSupplementsProvider = bulkImportSupplementsContext.Provider

function reducer(state: State, action: Actions): State {
  switch (action.type) {
    case UPDATE_DATA:
      return { ...state, ...action.payload }

    case ADD_SUPPLEMENT_TYPE:
      const supplementType = action.payload
      return {
        ...state,
        supplementTypes: [...state.supplementTypes, supplementType],
      }

    case UPDATE_SUPPLEMENT_TYPE:
      const updatedSupplementType = action.payload
      const supplementTypes = state.supplementTypes.map((element) =>
        element.id === updatedSupplementType?.id ? updatedSupplementType : element
      )
      return {
        ...state,
        supplementTypes,
      }

    case DELETE_SUPPLEMENT_TYPE:
      const id = action.payload.id
      // worksheetMatches is like { last_name: [1, 1, 'Doe'], 1669142039980: [1, 4, 200], ... }
      const { [id]: _, ...worksheetMatchesWithoutDeleted } = state.worksheetMatches
      const supplementTypesWithoutDeleted = state.supplementTypes.filter(
        (element) => element.id !== id
      )

      return {
        ...state,
        supplementTypes: supplementTypesWithoutDeleted,
        worksheetMatches: worksheetMatchesWithoutDeleted,
      }

    case SHOW_UPLOAD_MODAL:
      return { ...initialState, showUploadModal: true }

    case SHOW_ASSIGN_TAXONOMY_MODAL:
      return { ...state, showAssignTypeModal: true }

    case SHOW_DELETE_MODAL:
      return { ...state, showDeleteModal: true }

    case SHOW_MANAGED_IMPORTED_MODAL:
      return { ...state, showManageImportedModal: true }

    case RESET_DATA:
      return initialState

    default:
      throw new Error()
  }
}

interface Props {
  children: React.ReactNode
}

export const BulkImportSupplementsContextProvider = ({ children }: Props) => {
  const [state, dispatch] = React.useReducer<React.Reducer<State, Actions>>(reducer, initialState)

  return (
    <BulkImportSupplementsProvider value={{ ...state, supplementDispatch: dispatch }}>
      {children}
    </BulkImportSupplementsProvider>
  )
}

export const useBulkImportSupplements = () => {
  const bulkImportSupplements = React.useContext(bulkImportSupplementsContext)

  if (!bulkImportSupplements) {
    throw new Error(
      'useBulkImportSupplements hook must be used within a BulkImportSupplementsContextProvider'
    )
  }

  return bulkImportSupplements
}
